import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus, Package, Truck, Tag, Loader2, X, Search, Pencil, Trash2,
  AlertTriangle, TrendingDown, DollarSign, Store, ImagePlus,
  ChevronDown, ChevronRight, ArrowUpDown, ShoppingCart
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- Product Card ---
function ProductCard({ product, categories, suppliers, onEdit, onDelete }) {
  const cat = categories.find(c => c.id === product.category_id);
  const prodSuppliers = suppliers.filter(s => product.supplier_ids?.includes(s.id));

  return (
    <div data-testid={`product-card-${product.id}`}
      className="bg-white border border-stone-200/60 rounded-xl overflow-hidden hover:shadow-sm transition-all group">
      {/* Image */}
      <div className="h-32 bg-stone-100 flex items-center justify-center overflow-hidden">
        {product.image_url ? (
          <img src={`${API.replace('/api', '')}${product.image_url}?t=${Date.now()}`} alt={product.name}
            className="w-full h-full object-cover" />
        ) : (
          <Package className="w-8 h-8 text-stone-300" />
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-stone-900 truncate">{product.name}</h4>
            {product.sku && <p className="text-[11px] text-stone-400 mt-0.5">SKU: {product.sku}</p>}
          </div>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={() => onEdit(product)} className="p-1 text-stone-400 hover:text-stone-600"><Pencil className="w-3.5 h-3.5" /></button>
            <button onClick={() => onDelete(product.id)} className="p-1 text-stone-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        </div>
        {product.description && <p className="text-xs text-stone-500 mt-1 line-clamp-2">{product.description}</p>}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {cat && (
            <span className="text-[10px] px-2 py-0.5 rounded-full border" style={{ borderColor: cat.color, color: cat.color, backgroundColor: cat.color + "15" }}>
              {cat.name}
            </span>
          )}
          {product.status !== "active" && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-500">{product.status}</span>
          )}
          {product.unit && <span className="text-[10px] text-stone-400">per {product.unit}</span>}
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-stone-100">
          <div>
            {product.price != null && <span className="text-sm font-bold text-stone-900">${product.price.toFixed(2)}</span>}
            {product.cost != null && <span className="text-[11px] text-stone-400 ml-2">Cost: ${product.cost.toFixed(2)}</span>}
          </div>
          {prodSuppliers.length > 0 && (
            <span className="text-[10px] text-stone-400 flex items-center gap-1"><Truck className="w-3 h-3" />{prodSuppliers.length}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Product Form ---
function ProductForm({ categories, suppliers, onSubmit, onCancel, editProduct }) {
  const [form, setForm] = useState({
    name: editProduct?.name || "", description: editProduct?.description || "",
    category_id: editProduct?.category_id || "", price: editProduct?.price ?? "",
    cost: editProduct?.cost ?? "", sku: editProduct?.sku || "",
    unit: editProduct?.unit || "each", status: editProduct?.status || "active",
    supplier_ids: editProduct?.supplier_ids || [],
  });
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const toggleSupplier = (sid) => {
    setForm(f => ({ ...f, supplier_ids: f.supplier_ids.includes(sid) ? f.supplier_ids.filter(s => s !== sid) : [...f.supplier_ids, sid] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Product name is required");
    setSubmitting(true);
    const fd = new FormData();
    fd.append("name", form.name);
    fd.append("description", form.description);
    fd.append("category_id", form.category_id || "");
    fd.append("price", form.price || "");
    fd.append("cost", form.cost || "");
    fd.append("sku", form.sku);
    fd.append("unit", form.unit);
    fd.append("status", form.status);
    fd.append("supplier_ids", form.supplier_ids.join(","));
    if (imageFile) fd.append("image", imageFile);
    await onSubmit(fd);
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="product-form" className="bg-white border border-stone-200/60 rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-stone-800">{editProduct ? "Edit Product" : "New Product"}</h3>
        <button type="button" onClick={onCancel}><X className="w-4 h-4 text-stone-400" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] text-stone-500 font-medium mb-1 block">Name</label>
          <Input data-testid="product-name-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Product name" className="text-sm" />
        </div>
        <div>
          <label className="text-[11px] text-stone-500 font-medium mb-1 block">SKU</label>
          <Input value={form.sku} onChange={e => setForm(f => ({ ...f, sku: e.target.value }))} placeholder="SKU-001" className="text-sm" />
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] text-stone-500 font-medium mb-1 block">Description</label>
          <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Product description" className="text-sm min-h-[50px]" />
        </div>
        <div>
          <label className="text-[11px] text-stone-500 font-medium mb-1 block">Category</label>
          <select value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">None</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] text-stone-500 font-medium mb-1 block">Unit</label>
          <select value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white">
            {["each", "lb", "oz", "kg", "case", "bag", "gallon", "liter", "box"].map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] text-stone-500 font-medium mb-1 block">Sell Price ($)</label>
          <Input type="number" step="0.01" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} placeholder="0.00" className="text-sm" />
        </div>
        <div>
          <label className="text-[11px] text-stone-500 font-medium mb-1 block">Cost ($)</label>
          <Input type="number" step="0.01" value={form.cost} onChange={e => setForm(f => ({ ...f, cost: e.target.value }))} placeholder="0.00" className="text-sm" />
        </div>
        <div>
          <label className="text-[11px] text-stone-500 font-medium mb-1 block">Status</label>
          <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="discontinued">Discontinued</option>
          </select>
        </div>
        <div>
          <label className="text-[11px] text-stone-500 font-medium mb-1 block">Product Image</label>
          <label className="flex items-center gap-2 border border-dashed border-stone-300 rounded-lg px-3 py-2 cursor-pointer hover:bg-stone-50 text-sm text-stone-500">
            <ImagePlus className="w-4 h-4" /> {imageFile ? imageFile.name : "Choose image..."}
            <input type="file" accept="image/*" onChange={e => setImageFile(e.target.files?.[0] || null)} className="hidden" />
          </label>
        </div>
      </div>
      {suppliers.length > 0 && (
        <div>
          <label className="text-[11px] text-stone-500 font-medium mb-2 block">Suppliers</label>
          <div className="flex flex-wrap gap-2">
            {suppliers.map(s => (
              <button key={s.id} type="button" onClick={() => toggleSupplier(s.id)}
                className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${form.supplier_ids.includes(s.id) ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-stone-50 border-stone-200 text-stone-600 hover:bg-stone-100"}`}>
                <Truck className="w-3 h-3 inline mr-1" />{s.name}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={submitting} className="bg-stone-900 text-white hover:bg-stone-800" data-testid="submit-product-btn">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
          {editProduct ? "Update" : "Add Product"}
        </Button>
      </div>
    </form>
  );
}

// --- Supplier Card ---
function SupplierCard({ supplier, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div data-testid={`supplier-card-${supplier.id}`} className="bg-white border border-stone-200/60 rounded-xl p-4 hover:shadow-sm transition-all">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Truck className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-stone-900">{supplier.name}</h4>
            {supplier.contact_name && <p className="text-xs text-stone-500">{supplier.contact_name}</p>}
            <div className="flex items-center gap-3 mt-1.5">
              {supplier.email && <span className="text-[11px] text-stone-400">{supplier.email}</span>}
              {supplier.phone && <span className="text-[11px] text-stone-400">{supplier.phone}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-stone-400">{supplier.product_count || 0} products</span>
          <button onClick={() => onEdit(supplier)} className="p-1 text-stone-400 hover:text-stone-600"><Pencil className="w-3.5 h-3.5" /></button>
          <button onClick={() => onDelete(supplier.id)} className="p-1 text-stone-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
          <button onClick={() => setExpanded(!expanded)} className="p-1 text-stone-400">
            {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="mt-3 pt-3 border-t border-stone-100 space-y-1">
          {supplier.address && <p className="text-xs text-stone-500"><strong>Address:</strong> {supplier.address}</p>}
          {supplier.notes && <p className="text-xs text-stone-500"><strong>Notes:</strong> {supplier.notes}</p>}
        </div>
      )}
    </div>
  );
}

// --- Supplier Form ---
function SupplierForm({ onSubmit, onCancel, editSupplier }) {
  const [form, setForm] = useState({
    name: editSupplier?.name || "", contact_name: editSupplier?.contact_name || "",
    email: editSupplier?.email || "", phone: editSupplier?.phone || "",
    address: editSupplier?.address || "", notes: editSupplier?.notes || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Supplier name is required");
    setSubmitting(true);
    await onSubmit(form);
    setSubmitting(false);
  };
  return (
    <form onSubmit={handleSubmit} data-testid="supplier-form" className="bg-white border border-stone-200/60 rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-stone-800">{editSupplier ? "Edit Supplier" : "New Supplier"}</h3>
        <button type="button" onClick={onCancel}><X className="w-4 h-4 text-stone-400" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><label className="text-[11px] text-stone-500 font-medium mb-1 block">Company Name</label>
          <Input data-testid="supplier-name-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Supplier name" className="text-sm" /></div>
        <div><label className="text-[11px] text-stone-500 font-medium mb-1 block">Contact Person</label>
          <Input value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} placeholder="Contact name" className="text-sm" /></div>
        <div><label className="text-[11px] text-stone-500 font-medium mb-1 block">Email</label>
          <Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@supplier.com" className="text-sm" /></div>
        <div><label className="text-[11px] text-stone-500 font-medium mb-1 block">Phone</label>
          <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+1 234 567 8900" className="text-sm" /></div>
        <div className="md:col-span-2"><label className="text-[11px] text-stone-500 font-medium mb-1 block">Address</label>
          <Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Full address" className="text-sm" /></div>
        <div className="md:col-span-2"><label className="text-[11px] text-stone-500 font-medium mb-1 block">Notes</label>
          <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Any notes..." className="text-sm min-h-[50px]" /></div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={submitting} className="bg-stone-900 text-white hover:bg-stone-800" data-testid="submit-supplier-btn">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
          {editSupplier ? "Update" : "Add Supplier"}
        </Button>
      </div>
    </form>
  );
}

// --- Inventory Row ---
function InventoryRow({ item, onAdjust }) {
  const [showAdjust, setShowAdjust] = useState(false);
  const [adjQty, setAdjQty] = useState("");
  const [adjReason, setAdjReason] = useState("received");
  const [adjNotes, setAdjNotes] = useState("");
  const [adjusting, setAdjusting] = useState(false);

  const handleAdjust = async () => {
    if (!adjQty || isNaN(Number(adjQty))) return toast.error("Enter a valid quantity");
    setAdjusting(true);
    await onAdjust(item.product_id, item.store_id, {
      quantity: Number(adjQty),
      reason: adjReason,
      notes: adjNotes,
    });
    setAdjQty(""); setAdjNotes(""); setShowAdjust(false); setAdjusting(false);
  };

  return (
    <div className={`bg-white border rounded-xl p-4 ${item.is_low_stock ? "border-red-200 bg-red-50/30" : "border-stone-200/60"}`}>
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
          {item.product?.image_url ? (
            <img src={`${API.replace('/api', '')}${item.product.image_url}?t=${Date.now()}`} alt="" className="w-full h-full object-cover" />
          ) : (
            <Package className="w-5 h-5 text-stone-300" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-stone-900 truncate">{item.product?.name || "Unknown"}</p>
          <p className="text-[11px] text-stone-400">{item.product?.sku || ""} {item.store?.name && `at ${item.store.name}`}</p>
        </div>
        <div className="text-right">
          <p className={`text-lg font-bold ${item.is_low_stock ? "text-red-600" : "text-stone-900"}`}>{item.quantity}</p>
          <p className="text-[10px] text-stone-400">/ {item.reorder_point} min</p>
        </div>
        {item.is_low_stock && <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />}
        <Button variant="ghost" size="sm" onClick={() => setShowAdjust(!showAdjust)} className="text-xs h-8">
          <ArrowUpDown className="w-3.5 h-3.5 mr-1" /> Adjust
        </Button>
      </div>
      {showAdjust && (
        <div className="mt-3 pt-3 border-t border-stone-100 flex gap-2 items-end flex-wrap">
          <div>
            <label className="text-[10px] text-stone-500 block mb-0.5">Qty (+/-)</label>
            <Input value={adjQty} onChange={e => setAdjQty(e.target.value)} type="number" placeholder="+10 or -5" className="text-xs h-8 w-24" />
          </div>
          <div>
            <label className="text-[10px] text-stone-500 block mb-0.5">Reason</label>
            <select value={adjReason} onChange={e => setAdjReason(e.target.value)} className="border border-stone-200 rounded-lg px-2 py-1 text-xs bg-white h-8">
              <option value="received">Received</option><option value="used">Used</option>
              <option value="wasted">Wasted</option><option value="transferred">Transferred</option>
              <option value="adjustment">Adjustment</option>
            </select>
          </div>
          <div className="flex-1 min-w-[120px]">
            <label className="text-[10px] text-stone-500 block mb-0.5">Notes</label>
            <Input value={adjNotes} onChange={e => setAdjNotes(e.target.value)} placeholder="Optional" className="text-xs h-8" />
          </div>
          <Button size="sm" onClick={handleAdjust} disabled={adjusting} className="bg-stone-900 text-white h-8 text-xs">
            {adjusting ? <Loader2 className="w-3 h-3 animate-spin" /> : "Apply"}
          </Button>
        </div>
      )}
    </div>
  );
}

// --- Category Manager ---
function ProductCategoryManager({ categories, onAdd, onDelete }) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("#6b7280");
  const [showForm, setShowForm] = useState(false);
  const handleAdd = async () => {
    if (!name.trim()) return;
    await onAdd({ name: name.trim(), color });
    setName(""); setColor("#6b7280"); setShowForm(false);
  };
  return (
    <Card className="border-stone-200/60">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-stone-800 flex items-center gap-2"><Tag className="w-4 h-4" /> Categories</h3>
          <Button size="sm" variant="ghost" onClick={() => setShowForm(!showForm)} className="text-xs h-7"><Plus className="w-3 h-3 mr-1" /> Add</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {categories.map(c => (
            <div key={c.id} className="flex items-center gap-1.5 px-2.5 py-1 bg-stone-50 rounded-full border border-stone-200 group">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color }} />
              <span className="text-xs text-stone-700">{c.name}</span>
              <button onClick={() => onDelete(c.id)} className="opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3 text-stone-400 hover:text-red-500" /></button>
            </div>
          ))}
          {categories.length === 0 && <p className="text-xs text-stone-400">No categories yet</p>}
        </div>
        {showForm && (
          <div className="flex gap-2 items-end mt-3 pt-3 border-t border-stone-100">
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Category name" className="text-xs h-8 flex-1" onKeyDown={e => e.key === "Enter" && handleAdd()} />
            <input type="color" value={color} onChange={e => setColor(e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0" />
            <Button size="sm" onClick={handleAdd} className="h-8 text-xs bg-stone-900 text-white"><Plus className="w-3 h-3" /></Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// --- Main Component ---
export default function Products() {
  const { isOwner } = useAuth();
  const [activeTab, setActiveTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stores, setStores] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStore, setFilterStore] = useState("");
  const [showLowOnly, setShowLowOnly] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      if (filterCategory) params.append("category_id", filterCategory);

      const invParams = new URLSearchParams();
      if (filterStore) invParams.append("store_id", filterStore);
      if (showLowOnly) invParams.append("low_stock", "true");

      const [prodRes, catRes, supRes, storeRes, statsRes, invRes] = await Promise.all([
        axios.get(`${API}/products?${params}`),
        axios.get(`${API}/products/categories`),
        axios.get(`${API}/products/suppliers`),
        axios.get(`${API}/stores`),
        axios.get(`${API}/products/stats`),
        axios.get(`${API}/products/inventory/all?${invParams}`),
      ]);
      setProducts(prodRes.data);
      setCategories(catRes.data);
      setSuppliers(supRes.data);
      setStores(storeRes.data);
      setStats(statsRes.data);
      setInventory(invRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [searchQuery, filterCategory, filterStore, showLowOnly]);

  useEffect(() => { loadData(); }, [loadData]);

  // Product actions
  const createProduct = async (fd) => {
    try { await axios.post(`${API}/products`, fd); toast.success("Product added!"); setShowForm(false); loadData(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const updateProduct = async (fd) => {
    try { await axios.put(`${API}/products/${editItem.id}`, fd); toast.success("Product updated!"); setEditItem(null); setShowForm(false); loadData(); }
    catch (e) { toast.error("Failed to update"); }
  };
  const deleteProduct = async (id) => {
    try { await axios.delete(`${API}/products/${id}`); toast.success("Product deleted"); loadData(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  // Supplier actions
  const createSupplier = async (data) => {
    try { await axios.post(`${API}/products/suppliers`, data); toast.success("Supplier added!"); setShowForm(false); loadData(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const updateSupplier = async (data) => {
    try { await axios.put(`${API}/products/suppliers/${editItem.id}`, data); toast.success("Supplier updated!"); setEditItem(null); setShowForm(false); loadData(); }
    catch (e) { toast.error("Failed"); }
  };
  const deleteSupplier = async (id) => {
    try { await axios.delete(`${API}/products/suppliers/${id}`); toast.success("Supplier deleted"); loadData(); }
    catch (e) { toast.error("Failed"); }
  };

  // Category actions
  const addCategory = async (data) => {
    try { await axios.post(`${API}/products/categories`, data); toast.success("Category added"); loadData(); }
    catch (e) { toast.error("Failed"); }
  };
  const deleteCategory = async (id) => {
    try { await axios.delete(`${API}/products/categories/${id}`); loadData(); }
    catch (e) { toast.error("Failed"); }
  };

  // Inventory actions
  const adjustStock = async (productId, storeId, data) => {
    try { await axios.post(`${API}/products/inventory/${productId}/${storeId}/adjust`, data); toast.success("Stock adjusted"); loadData(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-stone-400" /></div>;

  const actionBtn = () => {
    if (activeTab === "products") return (<Button data-testid="add-product-btn" onClick={() => { setShowForm(true); setEditItem(null); }} className="bg-stone-900 text-white hover:bg-stone-800"><Plus className="w-4 h-4 mr-2" /> Add Product</Button>);
    if (activeTab === "suppliers") return (<Button data-testid="add-supplier-btn" onClick={() => { setShowForm(true); setEditItem(null); }} className="bg-stone-900 text-white hover:bg-stone-800"><Plus className="w-4 h-4 mr-2" /> Add Supplier</Button>);
    return null;
  };

  return (
    <div className="px-6 md:px-12 lg:px-16 py-10 bg-stone-50 min-h-screen" data-testid="products-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>Products</h1>
          <p className="text-sm text-stone-500 mt-1">Manage products, inventory & suppliers</p>
        </div>
        {actionBtn()}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <div className="bg-white border border-stone-200/60 rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-stone-100"><Package className="w-5 h-5 text-stone-600" /></div>
            <div><p className="text-2xl font-bold text-stone-900">{stats.total_products}</p><p className="text-xs text-stone-500">Products</p></div>
          </div>
          <div className="bg-white border border-stone-200/60 rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-stone-100"><ShoppingCart className="w-5 h-5 text-emerald-500" /></div>
            <div><p className="text-2xl font-bold text-stone-900">{stats.active_products}</p><p className="text-xs text-stone-500">Active</p></div>
          </div>
          <div className="bg-white border border-stone-200/60 rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-stone-100"><Truck className="w-5 h-5 text-amber-500" /></div>
            <div><p className="text-2xl font-bold text-stone-900">{stats.suppliers}</p><p className="text-xs text-stone-500">Suppliers</p></div>
          </div>
          <div className="bg-white border border-stone-200/60 rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-stone-100"><Tag className="w-5 h-5 text-blue-500" /></div>
            <div><p className="text-2xl font-bold text-stone-900">{stats.categories}</p><p className="text-xs text-stone-500">Categories</p></div>
          </div>
          <div className={`bg-white border rounded-xl p-4 flex items-center gap-3 ${stats.low_stock_alerts > 0 ? "border-red-200 bg-red-50/30" : "border-stone-200/60"}`}>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-stone-100"><AlertTriangle className={`w-5 h-5 ${stats.low_stock_alerts > 0 ? "text-red-500" : "text-stone-400"}`} /></div>
            <div><p className={`text-2xl font-bold ${stats.low_stock_alerts > 0 ? "text-red-600" : "text-stone-900"}`}>{stats.low_stock_alerts}</p><p className="text-xs text-stone-500">Low Stock</p></div>
          </div>
          <div className="bg-white border border-stone-200/60 rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-stone-100"><DollarSign className="w-5 h-5 text-emerald-600" /></div>
            <div><p className="text-2xl font-bold text-stone-900">${stats.total_inventory_value.toLocaleString()}</p><p className="text-xs text-stone-500">Inv. Value</p></div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-stone-100 rounded-lg p-1 w-fit mb-6" data-testid="product-tabs">
        {[
          { key: "products", icon: Package, label: "Products", count: products.length },
          { key: "inventory", icon: TrendingDown, label: "Inventory", count: inventory.length },
          { key: "suppliers", icon: Truck, label: "Suppliers", count: suppliers.length },
        ].map(t => (
          <button key={t.key} onClick={() => { setActiveTab(t.key); setShowForm(false); setEditItem(null); }}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === t.key ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
            data-testid={`tab-${t.key}`}>
            <t.icon className="w-4 h-4 inline mr-1.5" />{t.label}
            {t.count > 0 && <span className="ml-1.5 text-xs bg-stone-200 text-stone-600 rounded-full px-1.5">{t.count}</span>}
          </button>
        ))}
      </div>

      {/* Products Tab */}
      {activeTab === "products" && (
        <div>
          {(showForm || editItem) && (
            <div className="mb-4">
              <ProductForm categories={categories} suppliers={suppliers}
                editProduct={editItem} onSubmit={editItem ? updateProduct : createProduct}
                onCancel={() => { setShowForm(false); setEditItem(null); }} />
            </div>
          )}
          <div className="flex gap-3 mb-4 flex-wrap items-center">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search products..." className="pl-9 text-sm" data-testid="product-search" />
            </div>
            {categories.length > 0 && (
              <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
                className="border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">All Categories</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            <ProductCategoryManager categories={categories} onAdd={addCategory} onDelete={deleteCategory} />
          </div>
          {products.length === 0 ? (
            <div className="text-center py-16 bg-white border border-stone-200/60 rounded-xl">
              <Package className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-stone-700 mb-1">No products yet</h3>
              <p className="text-sm text-stone-500 mb-4">Add your first product to get started</p>
              <Button onClick={() => setShowForm(true)} className="bg-stone-900 text-white"><Plus className="w-4 h-4 mr-2" /> Add Product</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {products.map(p => (
                <ProductCard key={p.id} product={p} categories={categories} suppliers={suppliers}
                  onEdit={prod => { setEditItem(prod); setShowForm(true); }} onDelete={deleteProduct} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Inventory Tab */}
      {activeTab === "inventory" && (
        <div>
          <div className="flex gap-3 mb-4 flex-wrap items-center">
            {stores.length > 0 && (
              <select value={filterStore} onChange={e => setFilterStore(e.target.value)}
                className="border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white" data-testid="inventory-store-filter">
                <option value="">All Stores</option>
                {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
            <button onClick={() => setShowLowOnly(!showLowOnly)}
              className={`text-sm px-3 py-2 rounded-lg border transition-all ${showLowOnly ? "bg-red-50 border-red-200 text-red-600" : "border-stone-200 text-stone-600 hover:bg-stone-50"}`}
              data-testid="low-stock-toggle">
              <AlertTriangle className="w-4 h-4 inline mr-1" /> Low Stock Only
            </button>
          </div>
          {inventory.length === 0 ? (
            <div className="text-center py-16 bg-white border border-stone-200/60 rounded-xl">
              <TrendingDown className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-stone-700 mb-1">{showLowOnly ? "No low stock items" : "No inventory records"}</h3>
              <p className="text-sm text-stone-500">Set stock levels on your products to track inventory</p>
            </div>
          ) : (
            <div className="space-y-2">
              {inventory.map((item, idx) => (
                <InventoryRow key={item.id || idx} item={item} onAdjust={adjustStock} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Suppliers Tab */}
      {activeTab === "suppliers" && (
        <div>
          {(showForm || editItem) && (
            <div className="mb-4">
              <SupplierForm editSupplier={editItem} onSubmit={editItem ? updateSupplier : createSupplier}
                onCancel={() => { setShowForm(false); setEditItem(null); }} />
            </div>
          )}
          {suppliers.length === 0 && !showForm ? (
            <div className="text-center py-16 bg-white border border-stone-200/60 rounded-xl">
              <Truck className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-stone-700 mb-1">No suppliers yet</h3>
              <p className="text-sm text-stone-500 mb-4">Add your first supplier</p>
              <Button onClick={() => setShowForm(true)} className="bg-stone-900 text-white"><Plus className="w-4 h-4 mr-2" /> Add Supplier</Button>
            </div>
          ) : (
            <div className="space-y-3">
              {suppliers.map(s => (
                <SupplierCard key={s.id} supplier={s}
                  onEdit={sup => { setEditItem(sup); setShowForm(true); }} onDelete={deleteSupplier} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
